// src/components/modules/TAI/PlanillaTAIEstadoModal.js

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  message,
  Typography,
  Tag,
  Divider,
  Alert,
  Steps,
} from "antd";
import {
  CheckCircleOutlined,
  InboxOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import api from "../../../axiosConfig";

const { Text, Title } = Typography;
const { TextArea } = Input;

// Configuración de estados con íconos y colores (solo 3 estados)
const ESTADO_CONFIG = {
  generada: {
    color: "#8c8c8c",
    bg: "#fafafa",
    label: "Generada",
    icon: <FileTextOutlined />,
    siguientes: ["recibida"],
  },
  recibida: {
    color: "#52c41a",
    bg: "#f6ffed",
    label: "Recibida",
    icon: <InboxOutlined />,
    siguientes: ["facturada"],
  },
  facturada: {
    color: "#722ed1",
    bg: "#f9f0ff",
    label: "Facturada",
    icon: <FileTextOutlined />,
    siguientes: [],
  },
};

// Orden de estados para el Steps
const ORDEN_ESTADOS = [
  "generada",
  "recibida",
  "facturada",
];

const PlanillaTAIEstadoModal = ({ open, onClose, planilla, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState(null);

  // Estado actual de la planilla
  const estadoActual = planilla?.estado || "generada";
  const estadoConfig = ESTADO_CONFIG[estadoActual];

  // Estados disponibles para transición
  const estadosDisponibles = useMemo(() => {
    return estadoConfig?.siguientes || [];
  }, [estadoConfig]);

  // Índice actual en el flujo de estados
  const currentStepIndex = ORDEN_ESTADOS.indexOf(estadoActual);

  // Determinar si se requieren campos adicionales
  const camposRequeridos = useMemo(() => {
    if (!nuevoEstado) return [];
    return ESTADO_CONFIG[nuevoEstado]?.campos || [];
  }, [nuevoEstado]);

  // Manejar cambio de estado
  const handleCambiarEstado = async () => {
    if (!nuevoEstado) {
      message.warning("Seleccione un estado");
      return;
    }

    try {
      // Validar campos adicionales si existen
      if (camposRequeridos.length > 0) {
        await form.validateFields();
      }

      setLoading(true);

      const payload = {
        estado: nuevoEstado,
        ...form.getFieldsValue(),
      };

      const response = await api.put(`/planillas-tai/${planilla.id}/estado`, payload);

      if (response.data.success) {
        message.success(`Estado actualizado a "${ESTADO_CONFIG[nuevoEstado]?.label}"`);
        onSuccess();
      }
    } catch (error) {
      if (error.errorFields) {
        // Error de validación del formulario
        return;
      }
      console.error("Error al cambiar estado:", error);
      message.error(error.response?.data?.message || "Error al cambiar estado");
    } finally {
      setLoading(false);
    }
  };

  // Renderizar formulario de observaciones (opcional)
  const renderCamposAdicionales = () => {
    if (!nuevoEstado) return null;

    return (
      <>
        <Divider />
        <Form form={form} layout="vertical">
          <Form.Item name="observaciones" label="Observaciones">
            <TextArea
              rows={2}
              placeholder="Observaciones adicionales (opcional)"
            />
          </Form.Item>
        </Form>
      </>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#722ed1" }} />
          <span>Cambiar Estado de Planilla</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={700}
      footer={
        <Space>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            type="primary"
            onClick={handleCambiarEstado}
            loading={loading}
            disabled={!nuevoEstado}
            style={{ backgroundColor: "#722ed1" }}
          >
            Actualizar Estado
          </Button>
        </Space>
      }
      destroyOnClose
    >
      {/* Información de la planilla */}
      <div style={{ marginBottom: 16 }}>
        <Space size={16}>
          <Tag color="#722ed1" style={{ fontWeight: "bold", fontSize: 14 }}>
            {planilla?.codigo_planilla}
          </Tag>
          <Text>
            {planilla?.paciente?.nombre || "Sin paciente"}
          </Text>
          <Text type="secondary">
            {dayjs(planilla?.fecha_servicio).format("DD/MM/YYYY")}
          </Text>
        </Space>
      </div>

      {/* Flujo de estados */}
      <Steps
        current={currentStepIndex}
        size="small"
        style={{ marginBottom: 24 }}
        items={ORDEN_ESTADOS.map((estado) => {
          const config = ESTADO_CONFIG[estado];
          const isAnulada = estadoActual === "anulada";

          return {
            title: config.label,
            icon: config.icon,
            status: isAnulada
              ? "error"
              : estado === estadoActual
              ? "process"
              : ORDEN_ESTADOS.indexOf(estado) < currentStepIndex
              ? "finish"
              : "wait",
          };
        })}
      />

      {estadoActual === "anulada" && (
        <Alert
          message="Planilla Anulada"
          description="Esta planilla ha sido anulada y no se pueden realizar más cambios de estado."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {estadoActual === "facturada" && (
        <Alert
          message="Planilla Facturada"
          description="Esta planilla ya ha sido facturada. No se permiten más cambios de estado."
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {estadosDisponibles.length > 0 && (
        <>
          {/* Selección de nuevo estado */}
          <div style={{ marginBottom: 16 }}>
            <Title level={5}>Seleccione el nuevo estado:</Title>
            <Space size={[8, 8]} wrap>
              {estadosDisponibles.map((estado) => {
                const config = ESTADO_CONFIG[estado];
                const isSelected = nuevoEstado === estado;

                return (
                  <Button
                    key={estado}
                    type={isSelected ? "primary" : "default"}
                    icon={config.icon}
                    onClick={() => {
                      setNuevoEstado(estado);
                      form.resetFields();
                    }}
                    style={{
                      backgroundColor: isSelected ? config.color : undefined,
                      borderColor: config.color,
                      color: isSelected ? "#fff" : config.color,
                    }}
                  >
                    {config.label}
                  </Button>
                );
              })}
            </Space>
          </div>

          {/* Indicador de transición */}
          {nuevoEstado && (
            <Alert
              message={
                <Space>
                  <Tag
                    color={estadoConfig.color}
                    style={{ backgroundColor: estadoConfig.bg }}
                  >
                    {estadoConfig.label}
                  </Tag>
                  <ArrowRightOutlined />
                  <Tag
                    color={ESTADO_CONFIG[nuevoEstado].color}
                    style={{ backgroundColor: ESTADO_CONFIG[nuevoEstado].bg }}
                  >
                    {ESTADO_CONFIG[nuevoEstado].label}
                  </Tag>
                </Space>
              }
              type="info"
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Campos adicionales según el estado */}
          {renderCamposAdicionales()}
        </>
      )}
    </Modal>
  );
};

PlanillaTAIEstadoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  planilla: PropTypes.shape({
    id: PropTypes.number.isRequired,
    codigo_planilla: PropTypes.string.isRequired,
    estado: PropTypes.string.isRequired,
    fecha_servicio: PropTypes.string,
    paciente: PropTypes.shape({
      nombre: PropTypes.string,
    }),
  }),
  onSuccess: PropTypes.func.isRequired,
};

export default PlanillaTAIEstadoModal;
